/**
 * ALTIORA · Impact — pourquoi vous ouvrez ALTIORA chaque matin.
 *
 * Dashboard 30 secondes affichant les 6 modules du Impact Engine :
 *   opportunity, case_study, regulatory_risk, skill_of_the_day,
 *   best_action_now, changes_since_yesterday.
 */
import { useEffect, useState } from "react";
import {
  TrendingUp, BookOpen, Shield, GraduationCap, Zap, Bell,
  RefreshCw, Sparkles, ChevronRight,
} from "lucide-react";
import {
  PageShell, PageHeader, Card, GhostButton, Loader,
  NAVY, CHAMPAGNE, IVORY, SLATE, NAVY_DARK as INK,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MODULE_META = {
  opportunity:       { icon: TrendingUp,   color: "#0a7f42",
                        label: "Opportunité" },
  case_study:        { icon: BookOpen,     color: "#0a2540",
                        label: "Étude de cas" },
  regulatory_risk:   { icon: Shield,       color: "#c95050",
                        label: "Risque réglementaire" },
  skill_of_the_day:  { icon: GraduationCap,color: "#c9a34e",
                        label: "Compétence" },
  best_action_now:   { icon: Zap,          color: "#8B4E00",
                        label: "Action prioritaire" },
  changes_since_yesterday: { icon: Bell,   color: "#2b57a8",
                              label: "Depuis hier" },
};

const MODULE_ORDER = [
  "best_action_now", "opportunity", "regulatory_risk",
  "case_study", "skill_of_the_day", "changes_since_yesterday",
];

const SOURCE_LABELS = {
  regulation_official: "Réglementaire",
  best_practice: "Bonne pratique",
  institution_doc: "Institution",
  professional_literature: "Littérature pro",
  altiora_original: "ALTIORA",
  jurisprudence: "Jurisprudence",
  field_experience: "Retour d'expérience",
};

export default function ImpactToday() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await api.get(
        `/impact-engine/today${force ? "?force_refresh=true" : ""}`
      );
      setData(data);
      if (force) toast.success("Boussole du jour actualisée");
    } catch { toast.error("Impossible de charger votre boussole"); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(false); }, []);

  if (loading) return (
    <PageShell><TopBar />
      <div className="max-w-6xl mx-auto py-16 flex justify-center">
        <Loader size={26} />
      </div>
    </PageShell>
  );
  if (!data) return null;

  const modules = data.modules || {};
  const learning = data.learning || {};

  return (
    <PageShell>
      <TopBar />
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="impact-root">
        <PageHeader
          eyebrow={`ALTIORA · Boussole du jour · ${data.date}`}
          title="Pourquoi ouvrir ALTIORA aujourd'hui"
          subtitle="Six essentiels personnalisés pour prendre 30 secondes d'avance sur votre journée."
          testid="impact-header"
        />

        {/* Ligne 1 : learning maturity + refresh */}
        <div className="flex flex-wrap items-center justify-between mt-4 gap-3">
          <div className="flex items-center gap-2 text-xs" data-testid="impact-learning">
            <Sparkles size={13} style={{ color: CHAMPAGNE }} />
            <span style={{ color: SLATE }}>
              Niveau d'apprentissage&nbsp;:
            </span>
            <span className="font-mono uppercase tracking-widest text-[10px]"
                  style={{ color: NAVY }}>
              {learning.maturity}
            </span>
            <div className="w-24 h-1 rounded-full overflow-hidden ml-2"
                 style={{ background: "#efe7cf" }}>
              <div className="h-full transition-all duration-500"
                   style={{ width: `${learning.readiness_percent || 0}%`,
                            background: CHAMPAGNE }} />
            </div>
            <span className="text-[10px]" style={{ color: SLATE }}>
              {learning.readiness_percent || 0}%
            </span>
          </div>
          <GhostButton onClick={() => load(true)} disabled={refreshing}
                        testid="impact-refresh">
            <RefreshCw size={12} /> {refreshing ? "Actualisation…" : "Actualiser"}
          </GhostButton>
        </div>

        {/* Grille des 6 modules */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {MODULE_ORDER.map((key) => {
            const mod = modules[key];
            if (!mod) return null;
            return <ImpactModuleCard key={key} moduleKey={key} module={mod} />;
          })}
        </div>
      </div>
    </PageShell>
  );
}

function ImpactModuleCard({ moduleKey, module }) {
  const meta = MODULE_META[moduleKey] || {};
  const Icon = meta.icon;
  const isChanges = moduleKey === "changes_since_yesterday";
  return (
    <Card className="p-5 flex flex-col h-full"
          testid={`impact-module-${moduleKey}`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && (
          <div className="w-8 h-8 rounded-full flex items-center justify-center"
               style={{ background: `${meta.color}15`,
                        border: `1px solid ${meta.color}66` }}>
            <Icon size={14} style={{ color: meta.color }} />
          </div>
        )}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest"
               style={{ color: meta.color }}>
            {meta.label}
          </div>
          <div className="text-sm font-serif" style={{ color: NAVY }}>
            {module.title}
          </div>
        </div>
      </div>

      {isChanges ? (
        <ul className="space-y-1 flex-1" data-testid="impact-changes-list">
          {(module.items || []).length === 0 ? (
            <li className="text-xs" style={{ color: SLATE }}>
              Aucun changement notable depuis hier.
            </li>
          ) : (
            (module.items || []).map((it) => (
              <li key={it.id} className="text-xs py-1 border-b flex justify-between"
                  style={{ borderColor: "#e5e0d0" }}>
                <span style={{ color: INK }}>{it.title}</span>
                <span className="text-[10px] font-mono" style={{ color: SLATE }}>
                  {SOURCE_LABELS[it.source_type] || it.source_type}
                </span>
              </li>
            ))
          )}
        </ul>
      ) : (
        <>
          {module.item ? (
            <>
              <div className="text-sm mt-1 flex-1" style={{ color: INK }}>
                {module.item.summary || module.item.title}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={{ background: "#f3ecd7", color: NAVY }}>
                  {SOURCE_LABELS[module.item.source_type] || module.item.source_type}
                </span>
                <span className="text-[10px]" style={{ color: SLATE }}>
                  Confiance {module.item.confidence}%
                </span>
              </div>
              <div className="text-[11px] italic mt-3" style={{ color: SLATE }}>
                {module.reason}
              </div>
              {module.cta && (
                <button
                  className="mt-3 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded"
                  style={{ background: NAVY, color: IVORY }}
                  data-testid={`impact-cta-${moduleKey}`}
                >
                  {module.cta} <ChevronRight size={12} />
                </button>
              )}
            </>
          ) : (
            <div className="text-xs flex-1" style={{ color: SLATE }}>
              Aucun élément identifié aujourd'hui pour ce module.
            </div>
          )}
        </>
      )}
    </Card>
  );
}
